import { useEffect, useCallback, useState } from "react";
import styled from "styled-components";
import { useNavigate } from 'react-router-dom';
import { APP_NAME } from '../constants/app';
import { useTranslation } from 'react-i18next';
import useUserStore from '../stores/userStore';
import useDailyRewardStore from '../stores/dailyRewardStore';
import useChallengeStore from '../stores/challengeStore';
import { useAdsgram } from '../hooks/useAdsgram';
import { useMonetag } from '../hooks/useMonetag';
import WebApp from '@twa-dev/sdk';
import { toast } from 'react-toastify';
import { apiService } from '../services/api';
import DailyRewardModal from '../components/DailyRewardModal';
import ReactPixel from 'react-facebook-pixel';
import HomeHeader from '../components/Home/HomeHeader.jsx';
import TasksSection, { CompletedTasksSection } from '../components/Home/TasksSection.jsx';
import { SectionTitle, TaskButton, TaskCard, TaskDescription, TaskIcon, TaskInfo, TaskReward } from "../components/Home/TasksSection.styled.js";
import { FaTrophy } from "react-icons/fa";
import { formatPrice } from "../utils/formatPrice.js";
import CurrencyIcon from "../components/CurrencyIcon.js";
import { IoChevronForward } from "react-icons/io5";

const ADSGRAM_REWARD_BLOCK_ID = '12244';
const ADSGRAM_INTERSTITIAL_BLOCK_ID = 'int-12245';

const ChallengeBlock = styled.div`
  margin: 32px 0 0 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const ChallengeTitle = styled.h2`
  color: #fff;
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 18px;
`;
const ChallengeCard = styled.div`
  background: #23233a;
  border-radius: 24px;
  padding: 28px 20px 20px 20px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 100%;
  cursor: pointer;
  margin-bottom: 18px;
`;
const ChallengePrize = styled.div`
  color: #fff;
  font-size: 20px;
  text-align: center;
  margin-bottom: 12px;
`;
const ChallengeImage = styled.img`
  width: 180px;
  height: 120px;
  object-fit: contain;
  margin: 0 auto 18px auto;
  display: block;
`;
const ChallengeButton = styled.button`
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 16px;
  padding: 16px 0;
  font-size: 18px;
  font-weight: 700;
  width: 100%;
  margin-top: 18px;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: #1d4ed8; }
`;
const ChallengeTimer = styled.div`
  color: #3b82f6;
  font-size: 22px;
  font-weight: 700;
  margin: 12px 0 0 0;
  letter-spacing: 1px;
  display: flex;
  gap: 18px;
  justify-content: center;
`;
const ChallengeSectionTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  margin: 32px 0 18px 0;
  color: #fff;
  text-align: left;
`;
const ChallengeTaskCard = styled.div`
  background: #23233a;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  cursor: pointer;
`;
const ChallengeIcon = styled.div`
  flex: 0 0 48px;
  width: 48px;
  height: 48px;
  background: #181622;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const ChallengeInfo = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const ChallengeName = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #fff;
`;
const ChallengePrizeTask = styled.div`
  font-size: 15px;
  color: #ffd700;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
`;
const ChallengeTimerTask = styled.div`
  font-size: 14px;
  color: #3b82f6;
  font-weight: 500;
`;
const ChallengeTaskButton = styled.button`
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 10px 18px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: #1d4ed8; }
`;

function getTimeLeft(end) {
	const now = Date.now();
	const endTime = new Date(end).getTime();
	let diff = Math.max(0, endTime - now);
	const hours = Math.floor(diff / 1000 / 60 / 60);
	diff -= hours * 60 * 60 * 1000;
	const minutes = Math.floor(diff / 1000 / 60);
	diff -= minutes * 60 * 1000;
	const seconds = Math.floor(diff / 1000);
	return { hours, minutes, seconds };
}

function formatTimer(timer) {
	if (timer.hours >= 24) {
		const days = Math.floor(timer.hours / 24);
		const hours = timer.hours % 24;
		return `${days}д ${hours}ч ${timer.minutes}м ${timer.seconds}с`;
	}
	return `${timer.hours}ч ${timer.minutes}м ${timer.seconds}с`;
}

function Home() {
	const user = useUserStore(state => state.user);
	const balance = user?.balance ? parseFloat(user.balance) : 0;
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { fetchStreak, setStreak } = useDailyRewardStore();
	const [subscriptionLoading, setSubscriptionLoading] = useState(false);
	const [showSubscribeButton, setShowSubscribeButton] = useState(false);
	const fetchUser = useUserStore(state => state.fetchUser);
	const [isRewardModalOpen, setIsRewardModalOpen] = useState(false);
	const streak = useDailyRewardStore(state => state.streak);
	const [adStats, setAdStats] = useState(null);
	const [adLoading, setAdLoading] = useState(false);
	const [monetagLoading, setMonetagLoading] = useState(false);
	const theme = useUserStore(state => state.theme);
	const { challenges, loading: challengesLoading, fetchChallenges } = useChallengeStore();
	const [timer, setTimer] = useState({ hours: 0, minutes: 0, seconds: 0 });
	const [timerInterval, setTimerInterval] = useState(null);

	const onReward = useCallback(async () => {
		setAdLoading(true);
		try {
			const stats = await apiService.getAdStats();
			setAdStats(stats);
			ReactPixel.track('ViewContent', { content_name: 'AdWatched' });
		} catch (e) {
			console.error('Ошибка при обновлении статистики рекламы:', e);
		} finally {
			setAdLoading(false);
		}
	}, []);

	const onMonetagReward = useCallback(async () => {
		setMonetagLoading(true);
		try {
			console.log('Monetag reward получен');
			ReactPixel.track('ViewContent', { content_name: 'AdWatched' });
		} catch (e) {
			console.error('Ошибка при обработке награды Monetag:', e);
		} finally {
			setMonetagLoading(false);
		}
	}, []);

	const showAdReward = useAdsgram({ blockId: ADSGRAM_REWARD_BLOCK_ID, onReward, fetchBalance: fetchUser });
	const showMonetagPopup = useMonetag({ onReward: onMonetagReward, fetchBalance: fetchUser });

	//Fetching streak for user
	useEffect(() => {
		if (user) {
			fetchStreak();
		}
	}, [fetchStreak, user]);

	//Fetching ad stats
	useEffect(() => {
		async function fetchAdStats() {
			try {
				const stats = await apiService.getAdStats();

				setAdStats(stats);
				console.log('Ad stats:', stats);
			} catch (e) {
				console.error('Ошибка при получении статистики рекламы:', e);
			}
		}
		fetchAdStats();
	}, []);

	useEffect(() => { fetchChallenges(); }, [fetchChallenges]);

	// Таймер для первого челенджа
	useEffect(() => {
		if (challenges.length > 0 && challenges[0].end_date) {
			const update = () => setTimer(getTimeLeft(challenges[0].end_date));
			update();
			if (timerInterval) clearInterval(timerInterval);
			const interval = setInterval(update, 1000);
			setTimerInterval(interval);
			return () => clearInterval(interval);
		}
	}, [challenges]);

	const handleBuyTokens = () => {
		navigate('/packages');
	};

	const getBotUsername = () => {
		return process.env.REACT_APP_BOT_USERNAME || '';
	};
	const handleShareReferral = () => {
		const botUsername = getBotUsername();
		const refLink = `https://t.me/${botUsername}?startapp=ref_${user.ref || ''}`;

		if (WebApp.isVersionAtLeast('6.0')) {
			WebApp.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(refLink)}&text=${encodeURIComponent(t('common.shareReferral'))}`);
		} else {
			navigator.clipboard.writeText(refLink);
			toast.info(t('common.linkCopied'));
		}
	};

	const handleSubscribe = () => {
		const channelUsername = process.env.REACT_APP_CHANNEL_USERNAME || 'skinforge';
		const channelUrl = `https://t.me/${channelUsername}`;
		window.open(channelUrl, '_blank');

		// Показываем кнопку проверки через 5 секунд
		setTimeout(() => {
			setShowSubscribeButton(true);
		}, 1000);
	};

	const handleCheckSubscription = async () => {
		try {
			setSubscriptionLoading(true);
			await apiService.checkSubscription();
			await fetchUser();
			setShowSubscribeButton(false);
			ReactPixel.track('Subscribe');
			toast.success(t('tasks.subscription_checked'));
		} catch (error) {
			toast.error(t('common.error'));
			setShowSubscribeButton(false);
		} finally {
			setSubscriptionLoading(false);
		}
	};

	const handleRewardIconClick = () => {
		setIsRewardModalOpen(true);
	};

	const handleOpenGroup = () => {
		const channelUsername = process.env.REACT_APP_CHANNEL_USERNAME || 'skinforge';
		const channelUrl = `https://t.me/${channelUsername}`;
		if (window.Telegram?.WebApp) {
			window.Telegram.WebApp.openTelegramLink(channelUrl);
		} else {
			window.open(channelUrl, '_blank');
		}
	};

	const handleShowAdWithDelay = () => {
		setTimeout(() => {
			showAdReward();
		}, 1000);
	};

	const handleShowMonetagWithDelay = () => {
		setTimeout(() => {
			showMonetagPopup();
		}, 1000);
	};

	return (
		<Container>
			<HomeHeader
				appName={APP_NAME}
				subtitle={t('tokens.description', { currency: t('currency_title') })}
				balance={balance}
				currencyTitle={t('currency_title')}
				onBuyTokens={handleBuyTokens}
				theme={theme}
				t={t}
			/>

			<TasksSection
				user={user}
				streak={streak}
				adStats={adStats}
				subscriptionLoading={subscriptionLoading}
				showSubscribeButton={showSubscribeButton}
				monetagLoading={monetagLoading}
				t={t}
				theme={theme}
				navigate={navigate}
				handleRewardIconClick={handleRewardIconClick}
				handleShareReferral={handleShareReferral}
				handleSubscribe={handleSubscribe}
				handleCheckSubscription={handleCheckSubscription}
				handleOpenGroup={handleOpenGroup}
				handleShowAdWithDelay={handleShowAdWithDelay}
				handleShowMonetagWithDelay={handleShowMonetagWithDelay}
			/>

			{/* Challenge block - между активными и выполненными заданиями */}
			{!challengesLoading && challenges.length > 0 && (
				<>
					<SectionTitle style={{ margin: '32px 0 18px 0' }} theme={theme}>
						{t('challenges_section_title')}
					</SectionTitle>
					<TaskCard onClick={() => navigate('/referrals-challenge')} style={{ cursor: 'pointer', marginBottom: 14 }} theme={theme}>
						<TaskIcon theme={theme}>
							<FaTrophy size={28} color="#facc15" />
						</TaskIcon>
						<TaskInfo>
							<TaskDescription theme={theme}>
								{challenges[0].reward_action && challenges[0].reward_action.code && challenges[0].reward_action.code.includes('REFERRAL_CHALLENGE')
									? t('referral_challenge_label')
									: challenges[0].name}
							</TaskDescription>
							<TaskReward theme={theme} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
								{formatPrice(challenges[0].total_reward)} <CurrencyIcon size={18} />
							</TaskReward>
							<TaskReward theme={theme} style={{ color: '#3b82f6', fontWeight: 500, fontSize: 14 }}>
								{formatTimer(timer)}
							</TaskReward>
						</TaskInfo>
						<TaskButton
							onClick={e => {
								e.stopPropagation();
								handleShareReferral();
							}}
							style={{ marginLeft: 'auto' }}
						>
							{t('tasks.invite_action')}
							<IoChevronForward size={18} />
						</TaskButton>
					</TaskCard>
				</>
			)}

			<CompletedTasksSection
				user={user}
				streak={streak}
				adStats={adStats}
				subscriptionLoading={subscriptionLoading}
				showSubscribeButton={showSubscribeButton}
				monetagLoading={monetagLoading}
				t={t}
				theme={theme}
				navigate={navigate}
				handleRewardIconClick={handleRewardIconClick}
				handleShareReferral={handleShareReferral}
				handleSubscribe={handleSubscribe}
				handleCheckSubscription={handleCheckSubscription}
				handleOpenGroup={handleOpenGroup}
				handleShowAdWithDelay={handleShowAdWithDelay}
				handleShowMonetagWithDelay={handleShowMonetagWithDelay}
			/>

			<DailyRewardModal
				isOpen={isRewardModalOpen}
				onClose={() => setIsRewardModalOpen(false)}
			/>
		</Container>
	);
}

const Container = styled.div`
	min-height: 100vh;
	background: ${props => props.theme.background};
	padding: 0 16px;
	box-sizing: border-box;
`;

export default Home;